import React, { Fragment, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ThemeProvider } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import ButtonBase from "@mui/material/ButtonBase";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";

import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined";
import AirportShuttleOutlinedIcon from "@mui/icons-material/AirportShuttleOutlined";
import SupervisorAccountOutlinedIcon from "@mui/icons-material/SupervisorAccountOutlined";
import FlagIcon from "@mui/icons-material/Flag";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import LocalPharmacyOutlinedIcon from "@mui/icons-material/LocalPharmacyOutlined";
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined";
import FormatListBulletedOutlinedIcon from "@mui/icons-material/FormatListBulletedOutlined";
import HourglassBottomOutlinedIcon from "@mui/icons-material/HourglassBottomOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { AuthContext } from "../../context/AuthContext";
import { ConfiguracionContext } from "../../context/ConfiguracionContext";
import { getBrand, createBrandTheme } from "../../theme/brand";
import Barra from "./Barra";
import logoAmbulanceApp from "../../assets/images/ambulanceapp.png";
import packageJson from "../../../package.json";

const ACCESOS = [
  {
    label: "Toma de Servicios",
    desc: "Ingreso de pedidos",
    to: "/tomaservicio",
    icon: <PhoneInTalkOutlinedIcon />,
  },
  {
    label: "Despachos",
    desc: "Operación en curso",
    to: "/despachos",
    icon: <AirportShuttleOutlinedIcon />,
  },
  {
    label: "Tripulaciones",
    desc: "Móviles y personal",
    to: "/tripulaciones",
    icon: <SupervisorAccountOutlinedIcon />,
  },
  {
    label: "Finalizados",
    desc: "Servicios cerrados",
    to: "/serviciosfinalizados",
    icon: <FlagIcon />,
  },
  {
    label: "Móviles",
    desc: "Elegí una opción",
    icon: <DirectionsCarFilledOutlinedIcon />,
    children: [
      { label: "Propios", to: "/movilespropios", icon: <DirectionsCarFilledOutlinedIcon fontSize="small" /> },
      { label: "Derivados", to: "/movilesderivados", icon: <LocalShippingOutlinedIcon fontSize="small" /> },
    ],
  },
  {
    label: "Medicamentos",
    desc: "Elegí una opción",
    icon: <LocalPharmacyOutlinedIcon />,
    children: [
      { label: "Almacenes", to: "/almacenes", icon: <WarehouseOutlinedIcon fontSize="small" /> },
      { label: "Medicamentos", to: "/medicamentos", icon: <LocalPharmacyOutlinedIcon fontSize="small" /> },
      { label: "Movimientos", to: "/movimientosmedicamentos", icon: <SwapHorizOutlinedIcon fontSize="small" /> },
      { label: "Stocks", to: "/stocksmedicamentos", icon: <Inventory2OutlinedIcon fontSize="small" /> },
      { label: "Ajustes de Stock", to: "/ajustesstocksmedicamentos", icon: <TuneOutlinedIcon fontSize="small" /> },
      { label: "Consumo de Medicamentos", to: "/consumomedicamentos", icon: <MedicationOutlinedIcon fontSize="small" /> },
    ],
  },
  {
    label: "Listados y Estadísticas",
    desc: "Elegí una opción",
    icon: <QueryStatsOutlinedIcon />,
    children: [
      { label: "Servicios", to: "/listadosservicios", icon: <FormatListBulletedOutlinedIcon fontSize="small" /> },
      { label: "Demoras", to: "/listadosdemoras", icon: <HourglassBottomOutlinedIcon fontSize="small" /> },
      { label: "Laborales", to: "/listadoslaborales", icon: <BadgeOutlinedIcon fontSize="small" /> },
      { label: "Traslados", to: "/listadostraslados", icon: <SwapHorizOutlinedIcon fontSize="small" /> },
      { label: "Medicamentos", to: "/listadosmedicamentos", icon: <LocalPharmacyOutlinedIcon fontSize="small" /> },
      { label: "Servicios Mes a Mes", to: "/estadisticaserviciosmesames", icon: <BarChartOutlinedIcon fontSize="small" /> },
      { label: "Servicios Semanales", to: "/estadisticaserviciossemanales", icon: <InsertChartOutlinedIcon fontSize="small" /> },
    ],
  },
];

export default function Logo() {
  const navigate = useNavigate();
  const { state } = useContext(AuthContext);
  const { configuracion } = useContext(ConfiguracionContext);

  const brand = getBrand(configuracion);
  const theme = createBrandTheme(brand);
  const autenticado = state && state.autenticado === true;

  const [menu, setMenu] = useState({ anchorEl: null, opciones: [] });
  const cerrarMenu = () => setMenu({ anchorEl: null, opciones: [] });

  const abrirAcceso = (e, acceso) => {
    if (acceso.children) {
      setMenu({ anchorEl: e.currentTarget, opciones: acceso.children });
    } else {
      navigate(acceso.to);
    }
  };

  useEffect(() => {
    const version = localStorage.getItem("version");
    if (version !== packageJson.version) {
      if ("caches" in window) {
        caches.keys().then((names) => {
          names.forEach((name) => caches.delete(name));
        });
        window.location.reload(true);
      }
      localStorage.clear();
      localStorage.setItem("version", packageJson.version);
    }
  }, []);

  // Sin sesión iniciada: solo el logo, sin menú ni accesos directos.
  if (!autenticado) {
    return (
      <Fragment>
        <Barra />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "70vh",
            px: 2,
          }}
        >
          <Box
            component="img"
            src={logoAmbulanceApp}
            alt="Ambulance"
            sx={{ width: { xs: "70%", sm: "40%" }, maxWidth: 420, height: "auto" }}
          />
        </Box>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Barra />

      <ThemeProvider theme={theme}>
        <Container maxWidth="md" sx={{ py: { xs: 3, sm: 5 } }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {state && state.nombre ? `Hola, ${state.nombre}` : "Bienvenido"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {brand.label} · Accesos directos
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {ACCESOS.map((a) => (
              <Grid item xs={6} sm={4} md={3} key={a.label}>
                <Paper
                  elevation={0}
                  component={ButtonBase}
                  onClick={(e) => abrirAcceso(e, a)}
                  sx={{
                    width: "100%",
                    height: "100%",
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 1,
                    textAlign: "left",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    transition: "border-color .15s, box-shadow .15s, transform .15s",
                    "&:hover": {
                      borderColor: "primary.main",
                      boxShadow: 3,
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      alignSelf: "stretch",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                      }}
                    >
                      {a.icon}
                    </Box>
                    {a.children ? (
                      <ExpandMoreIcon fontSize="small" sx={{ color: "text.disabled" }} />
                    ) : null}
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                      {a.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {a.desc}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Menu
            anchorEl={menu.anchorEl}
            open={Boolean(menu.anchorEl)}
            onClose={cerrarMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            {menu.opciones.map((o) => (
              <MenuItem
                key={o.to}
                onClick={() => {
                  cerrarMenu();
                  navigate(o.to);
                }}
              >
                <ListItemIcon>{o.icon}</ListItemIcon>
                <ListItemText>{o.label}</ListItemText>
              </MenuItem>
            ))}
          </Menu>
        </Container>
      </ThemeProvider>
    </Fragment>
  );
}
